/*
 * -----------------------------------------------------------------------
 *  <copyright file="grid-menu/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import cn from 'clsx';
import React from 'react';
import ActionsMenuV2 from '../../actions-menu-v2';
import { IActionItemV2 } from '../../expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import GridMenuItem from './grid-menu-item';
import { ExtraMenuItem, ExtraMenuItemPosition, GridMenuProps } from './grid-menu.interface';
import { useTranslation } from '@hooks/index';

const GridMenu = ({
    settingsMenu,
    switchMenu,
    viewOptionsMenu,
    clearFilters,
    appliedFilters,
    extraMenuItems,
    disableExpandMenu,
    checkedRows,
    hideDeletedToggle,
    showDeletedItems,
    showExport,
    exportCallback,
    hideFilter = false,
}: GridMenuProps) => {
    const { t } = useTranslation();

    const splitMenuItems = (menuItems: ExtraMenuItem[]) => {
        const itemsLeft: ExtraMenuItem[] = [];
        const itemsRight: ExtraMenuItem[] = [];
        const expandMenu: ExtraMenuItem[] = [];

        menuItems.forEach((item) => {
            switch (true) {
                case item.position === ExtraMenuItemPosition.left:
                    itemsLeft.push(item);
                    break;
                case item.position === ExtraMenuItemPosition.right:
                    itemsRight.push(item);
                    break;
                case item.position === ExtraMenuItemPosition.expandMenu:
                    expandMenu.push(item);
                    break;
                default:
                    break;
            }
        });

        return [itemsLeft, itemsRight, expandMenu];
    };

    const [itemsLeft, itemsRight, expandMenu] = splitMenuItems(extraMenuItems || []);

    const updateActions = (tableActions: IActionItemV2[]) =>
        tableActions.map((action) => {
            const updatedAction = { ...action };
            if (action.requireSelection) {
                // @ts-ignore
                updatedAction.enabled = action.enabled(checkedRows);
                updatedAction.onClick = () => action.onClick(checkedRows);
            }
            return updatedAction;
        });

    return (
        <>
            {switchMenu && (
                <div className="grid-header__item grid-header__item--no-padding grid-header__item--always-visible">
                    <div className="grid-action" id="switch-menu-action">
                        {switchMenu}
                    </div>
                </div>
            )}

            {!!viewOptionsMenu && (
                <div className="grid-header__item grid-header__item--no-padding">
                    <div className="grid-action" id="view-options-menu-action">
                        {viewOptionsMenu}
                    </div>
                </div>
            )}

            {itemsLeft.map((item, index) => (
                <GridMenuItem item={item} key={item?.tooltipTitle || index} />
            ))}

            {!hideDeletedToggle && !!showDeletedItems && (
                <GridMenuItem
                    item={{
                        tooltipTitle: t(
                            showDeletedItems.value ? 'common:hideDeletedRecords' : 'common:displayDeletedRecords',
                        ),
                        onClick: showDeletedItems.toggle,
                        icon: ['fal', showDeletedItems.value ? 'trash-xmark' : 'trash-check'],
                    }}
                    data-testid="toggle-deleted-records"
                />
            )}

            {settingsMenu && (
                <div className="grid-header__item grid-header__item--no-padding">
                    <div className="grid-action" id="settings-menu-action">
                        {settingsMenu}
                    </div>
                </div>
            )}

            {showExport && (
                <GridMenuItem
                    item={{
                        tooltipTitle: t('common:exportExcel'),
                        onClick: exportCallback,
                        icon: ['fal', 'file-excel'],
                    }}
                    data-testid="export-excel-button"
                />
            )}

            {!hideFilter && (
                <GridMenuItem
                    item={{
                        tooltipTitle: t('common:clearFilters'),
                        onClick: clearFilters,
                        icon: <span className="k-icon k-font-icon k-i-filter-clear" style={{ fontSize: '18px' }} />,
                        disabled: !appliedFilters?.length,
                    }}
                    data-testid="grid-clear-filters"
                />
            )}

            {itemsRight.map((item, index) => (
                <GridMenuItem item={item} key={item?.tooltipTitle || index} data-testid={item.testId} />
            ))}

            {expandMenu.map((item, index) => (
                <div
                    className={cn('grid-header__item grid-header__item--no-padding', item.customClassName)}
                    key={item?.tooltipTitle || index}
                >
                    <div className="grid-action">
                        <ActionsMenuV2
                            wrapperClassName="grid-header-actions"
                            actions={updateActions(item.expandMenuItems || [])}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            showSingleMenuItem
                            disabled={disableExpandMenu}
                        />
                    </div>
                </div>
            ))}
        </>
    );
};

export default GridMenu;
